/**
 * Weather rules as pure functions — shared by the simulation (sim/weather.ts and every system
 * that feels the weather), the AI (which must price moves exactly like the engine) and the client
 * (HUD readouts, renderer). Everything a rule needs is in the per-hex condition bits, which are
 * public (`PlayerView.weather.conditions`).
 *
 * Only +, −, ×, ÷ and sqrt are used on anything that reaches the state: those are exactly
 * rounded in every JS engine, so a replay recorded on the server re-simulates bit-identically in
 * any browser (Math.exp/sin/pow are not guaranteed to).
 */
import { LAPSE_RATE, SEA_LEVEL_ELEVATION, WEATHER_ATTACK, WEATHER_MOVE, WEATHER_SIGHT } from './constants';
import { HEX_W, type HexGrid, type HexId } from './hex';
import { Terrain, type WeatherCell } from './types';

// ---- condition bits -------------------------------------------------------------------------

/** Precipitation (rain, or snow when W_SNOWING is set too). */
export const W_RAIN = 1;
/** Heavy precipitation: storm (or blizzard with W_SNOWING). Always set together with W_RAIN. */
export const W_STORM = 2;
/** The precipitation falls as snow. */
export const W_SNOWING = 4;
/** Morning fog. */
export const W_FOG = 8;
/** Mud: soaked, unfrozen, snow-free plains / forest / hills. */
export const W_MUD = 16;
/** Snow cover. */
export const W_SNOW = 32;
/** Frozen ground: rivers bear, marsh counts as plains, no mud. */
export const W_FROZEN = 64;
/** Wet ground (cosmetic, below mud). */
export const W_WET = 128;

// ---- geometry -------------------------------------------------------------------------------

const coordCache = new WeakMap<HexGrid, Float64Array>();

/**
 * Hex centres in weather units (world / HEX_W): x at [2h], y at [2h+1]. One unit is the distance
 * between neighbouring hex centres, so radii read as "hexes".
 */
export function weatherCoords(grid: HexGrid): Float64Array {
  let c = coordCache.get(grid);
  if (!c) {
    c = new Float64Array(grid.count * 2);
    for (let h = 0; h < grid.count; h++) {
      c[h * 2] = grid.centers[h * 2] / HEX_W;
      c[h * 2 + 1] = grid.centers[h * 2 + 1] / HEX_W;
    }
    coordCache.set(grid, c);
  }
  return c;
}

const smooth = (s: number): number => s * s * (3 - 2 * s);

/** Life envelope of a cell: builds up over the first 20 % of its life, decays over the last 25 %. */
export function cellEnvelope(age: number, life: number): number {
  if (life <= 0) return 0;
  const p = age / life;
  if (p <= 0 || p >= 1) return 0;
  if (p < 0.2) return smooth(p / 0.2);
  if (p > 0.75) return smooth((1 - p) / 0.25);
  return 1;
}

/** Current strength (0..1) of a cell's core. */
export const cellIntensity = (c: Pick<WeatherCell, 'peak' | 'age' | 'life'>): number =>
  c.peak * cellEnvelope(c.age, c.life);

/** The shape of a cell (what `cellPrecipAt` needs). */
export type CellShape = Pick<WeatherCell, 'x' | 'y' | 'ux' | 'uy' | 'major' | 'minor'>;

/**
 * Precipitation of one cell at a point: intensity × (1 − q)² inside the ellipse q < 1, where q is
 * the squared elliptical distance. Smooth, compact, and cheap.
 */
export function cellPrecipAt(c: CellShape, intensity: number, x: number, y: number): number {
  const dx = x - c.x;
  const dy = y - c.y;
  const a = (dx * c.ux + dy * c.uy) / c.major;
  const b = (dy * c.ux - dx * c.uy) / c.minor;
  const q = a * a + b * b;
  if (q >= 1) return 0;
  const f = 1 - q;
  return intensity * f * f;
}

/** Precipitation (0..1) at a point: the strongest cell there. */
export function precipitationAt(cells: readonly WeatherCell[], x: number, y: number): number {
  let p = 0;
  for (const c of cells) {
    const v = cellPrecipAt(c, cellIntensity(c), x, y);
    if (v > p) p = v;
  }
  return p > 1 ? 1 : p;
}

// ---- temperature ----------------------------------------------------------------------------

/** Air temperature at a hex of this elevation when it is `airTemp` °C at sea level. */
export function hexTemperature(airTemp: number, elevation: number): number {
  const above = elevation > SEA_LEVEL_ELEVATION ? elevation - SEA_LEVEL_ELEVATION : 0;
  return airTemp - (LAPSE_RATE * above) / (255 - SEA_LEVEL_ELEVATION);
}

/**
 * Day–night temperature curve in −1..1 for a (fractional) hour of day: coldest at 05:00, warmest
 * at 15:00, smoothstep in between (a polynomial stand-in for a cosine).
 */
export function diurnalCurve(hour: number): number {
  const h = ((hour % 24) + 24) % 24;
  if (h >= 5 && h < 15) return -1 + 2 * smooth((h - 5) / 10);
  const s = (h >= 15 ? h - 15 : h + 9) / 14;
  return 1 - 2 * smooth(s);
}

// ---- rules ----------------------------------------------------------------------------------

/** Terrain as the rules see it: frozen marsh bears like plains. */
export function effectiveTerrain(terrain: number, cond: number): number {
  return terrain === Terrain.Marsh && cond & W_FROZEN ? Terrain.Plains : terrain;
}

/** A river edge between two hexes is frozen when the ground on both banks is. */
export function riverFrozen(conditions: Uint8Array | null | undefined, a: HexId, b: HexId): boolean {
  return !!conditions && (conditions[a] & conditions[b] & W_FROZEN) !== 0;
}

/**
 * Weather multiplier on a unit's step into a hex with conditions `cond`: precipitation slows
 * everyone (roads too); mud and snow cover only off-road, mud hurting vehicles most.
 */
export function weatherMoveFactor(cond: number, motorized: boolean, road: boolean): number {
  let m = cond & W_STORM ? WEATHER_MOVE.storm : cond & W_RAIN ? WEATHER_MOVE.rain : 1;
  if (!road) {
    if (cond & W_MUD) m *= motorized ? WEATHER_MOVE.mudMotor : WEATHER_MOVE.mudFoot;
    else if (cond & W_SNOW) m *= WEATHER_MOVE.snow;
  }
  return m;
}

/** Weather multiplier on an attack into a hex with conditions `cond`. */
export function weatherAttackFactor(cond: number, armored: boolean): number {
  let m = cond & W_STORM ? WEATHER_ATTACK.storm : cond & W_RAIN ? WEATHER_ATTACK.rain : 1;
  if (cond & W_FOG) m *= WEATHER_ATTACK.fog;
  if (armored && cond & W_MUD) m *= WEATHER_ATTACK.mudArmor;
  return m;
}

/** Sight radius lost by a spotter standing in a hex with conditions `cond`. */
export function weatherSightPenalty(cond: number): number {
  if (cond & W_FOG) return WEATHER_SIGHT.fog;
  if (cond & W_STORM) return WEATHER_SIGHT.storm;
  if (cond & W_RAIN) return WEATHER_SIGHT.rain;
  return 0;
}

/** Short name of the sky over a hex for readouts: "blizzard", "rain", "fog" … ("" = fair). */
export function conditionWord(cond: number): string {
  if (cond & W_STORM) return cond & W_SNOWING ? 'blizzard' : 'storm';
  if (cond & W_RAIN) return cond & W_SNOWING ? 'snow' : 'rain';
  if (cond & W_FOG) return 'fog';
  return '';
}
